import { Link } from 'react-router';
import { RefreshCcw, SlidersHorizontal } from 'lucide-react';
import { hasAnyPermission, hasPermission, PERMISSIONS } from '@helios/authz';
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import { changedSettingsFields, useSettingsDraft } from './useSettingsDraft';
import { SettingsDraftActions } from './SettingsDraftActions';
import { Badge, Button, ErrorState } from '@helios/ui';
import { useSettingsBootstrap, useUpdateOrganizationSettings } from '@/hooks/useSettings';
import { useAuth } from '@/features/auth/authContext';
import {
  OrganizationSettingsForm,
  SettingsLoadingState,
  SettingsMutationBanner,
  toOrganizationForm,
  type OrganizationForm,
} from '@/features/settings/settingsShared';
import { SettingsPageFrame, SettingsSection } from '@/features/settings/settingsChrome';

export function SettingsPage() {
  const { permissions } = useAuth();
  const canOpenEstimatingConfig = hasAnyPermission(permissions, [
    PERMISSIONS.ESTIMATING_SETTINGS_READ,
    PERMISSIONS.ESTIMATING_CRITERIA_PROFILES_READ,
  ]);
  const bootstrapQuery = useSettingsBootstrap();
  const updateOrganization = useUpdateOrganizationSettings();
  const [notice, setNotice] = useState<{ scope: string; message: string } | null>(null);

  const bootstrap = bootstrapQuery.data;
  const { identity, isCurrent } = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const draft = useSettingsDraft(
    bootstrap?.organization
      ? {
          form: toOrganizationForm(bootstrap.organization),
          version: bootstrap.organization.version,
        }
      : null,
  );
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.hash === '#sidebar-pages')
      void navigate('/settings/preferences#sidebar-pages', { replace: true });
  }, [location.hash, navigate]);
  const activeSection = ['company', 'workspace-plan'].includes(location.hash.slice(1))
    ? location.hash.slice(1)
    : 'company';

  if (bootstrapQuery.isLoading) {
    return <SettingsLoadingState label="Loading organization settings…" />;
  }

  if (!bootstrap || bootstrapQuery.isError) {
    return (
      <ErrorState
        title="Settings unavailable"
        description="The protected settings endpoint did not return a tenant-scoped settings snapshot."
        action={
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => void bootstrapQuery.refetch()}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }

  const handleOrganizationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft.form || !draft.baseline || draft.version === null || !draft.dirty) return;
    const origin = identity;
    try {
      const saved = await updateOrganization.mutateAsync({
        expectedVersion: draft.version,
        ...changedSettingsFields(draft.form, draft.baseline, [
          'name',
          'type',
          'timezone',
          'fiscalYearStartMonth',
          'auditRetentionYears',
        ] satisfies (keyof OrganizationForm)[]),
      });
      if (!isCurrent(origin)) return;
      draft.accept({ form: toOrganizationForm(saved), version: saved.version });
      setNotice({ scope: draft.scope, message: 'Organization settings saved.' });
    } catch {
      /* Keep edits for explicit conflict resolution. */
    }
  };

  const plan = bootstrap.tenant.plan?.trim() || null;

  return (
    <SettingsPageFrame
      eyebrow="Workspace"
      title="Organization"
      description="Company identity, workspace plan, fiscal settings, and audit retention policy."
      actions={
        <div className="flex flex-wrap gap-1.5">
          {hasPermission(permissions, PERMISSIONS.USERS_MANAGE) ? (
            <Button size="xs" variant="outline" asChild>
              <Link to="/settings/users">Manage users</Link>
            </Button>
          ) : null}
          {hasPermission(permissions, PERMISSIONS.ROLES_MANAGE) ? (
            <Button size="xs" variant="outline" asChild>
              <Link to="/settings/roles">Roles & permissions</Link>
            </Button>
          ) : null}
          {hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN) ? (
            <Button size="xs" variant="outline" asChild>
              <Link to="/settings/tenants">Invite subscriber</Link>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => void bootstrapQuery.refetch()}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      }
      activeSectionId={activeSection}
      onSectionChange={(id) => {
        void navigate(
          { pathname: location.pathname, search: location.search, hash: `#${id}` },
          { replace: true },
        );
      }}
      sections={[
        {
          id: 'company',
          label: 'Company',
          content: (
            <OrganizationSettingsForm
              form={draft.form}
              onChange={draft.change}
              onSubmit={handleOrganizationSubmit}
              saving={updateOrganization.isPending}
              dirty={draft.dirty}
            />
          ),
        },
        {
          id: 'workspace-plan',
          label: 'Workspace plan',
          content: (
            <SettingsSection
              id="workspace-plan"
              title="Workspace plan"
              description="Tenant.plan and status as stored. Invoice totals are omitted until a billing provider is wired."
            >
              <div className="grid gap-2 sm:grid-cols-4">
                <PlanCell
                  label="Workspace"
                  value={bootstrap.tenant.name}
                  hint={bootstrap.tenant.slug}
                />
                <PlanCell label="Plan" value={plan ?? 'No plan assigned'} />
                <div>
                  <p className="text-2xs text-muted-foreground font-medium uppercase tracking-wide">
                    Status
                  </p>
                  <Badge
                    variant={bootstrap.tenant.status === 'ACTIVE' ? 'success' : 'outline'}
                    className="mt-1"
                  >
                    {bootstrap.tenant.status}
                  </Badge>
                </div>
                {canOpenEstimatingConfig ? (
                  <div>
                    <p className="text-2xs text-muted-foreground font-medium uppercase tracking-wide">
                      Estimating
                    </p>
                    <Button size="xs" variant="outline" className="mt-1" asChild>
                      <Link to="/estimating/config">
                        <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
                        Open Config
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </SettingsSection>
          ),
        },
      ]}
    >
      <SettingsMutationBanner
        message={notice?.scope === draft.scope ? notice.message : null}
        error={updateOrganization.error}
      />
      <SettingsDraftActions
        dirty={draft.dirty}
        changedOnServer={draft.changedOnServer}
        disabled={updateOrganization.isPending}
        onRevert={() => {
          draft.accept();
          updateOrganization.reset();
          setNotice(null);
        }}
      />
    </SettingsPageFrame>
  );
}

function PlanCell({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0 break-words">
      <p className="text-2xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
      <p className="text-foreground mt-0.5 text-xs font-medium">{value}</p>
      {hint ? <p className="text-2xs text-muted-foreground font-mono">{hint}</p> : null}
    </div>
  );
}
